import { Component, ReactNode, ErrorInfo } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'root' | 'feature' | 'component';
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches React rendering errors.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary level="feature">
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 *
 * Levels:
 * - root: Catches catastrophic errors, shows full-page fallback
 * - feature: Catches feature-level errors, allows rest of app to work
 * - component: Catches component-level errors, minimal impact
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isDevelopment = import.meta.env.DEV;

    // Log to console in development
    if (isDevelopment) {
      console.error('Error Boundary caught an error:', error);
      console.error('Component Stack:', errorInfo.componentStack);
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you could log to external service here
    // e.g., Sentry, LogRocket, etc.
    if (!isDevelopment) {
      // TODO: Add production error logging
      // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }

    this.setState({ errorInfo });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  renderRootError(): ReactNode {
    const { error, errorInfo } = this.state;
    const isDevelopment = import.meta.env.DEV;

    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Something went wrong</Alert.Heading>
          <p>We're sorry, but the application encountered an unexpected error. Please try refreshing the page.</p>
          {isDevelopment && error && (
            <div className="mt-3">
              <details>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details (Development Only)</summary>
                <pre className="mt-2" style={{ fontSize: '0.85rem' }}>
                  {error.toString()}
                  {errorInfo?.componentStack}
                </pre>
              </details>
            </div>
          )}
          <hr />
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
            <Button variant="outline-secondary" onClick={this.handleReset}>
              Try Again
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  renderFeatureError(): ReactNode {
    const { error, errorInfo } = this.state;
    const isDevelopment = import.meta.env.DEV;

    return (
      <Alert variant="warning" className="m-3">
        <Alert.Heading>Unable to load this section</Alert.Heading>
        <p>This part of the application encountered an error. The rest of the site should still work normally.</p>
        {isDevelopment && error && (
          <div className="mt-3">
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details (Development Only)</summary>
              <pre className="mt-2" style={{ fontSize: '0.85rem' }}>
                {error.toString()}
                {errorInfo?.componentStack}
              </pre>
            </details>
          </div>
        )}
        <hr />
        <Button variant="warning" size="sm" onClick={this.handleReset}>
          Try Again
        </Button>
      </Alert>
    );
  }

  renderComponentError(): ReactNode {
    const { error } = this.state;
    const isDevelopment = import.meta.env.DEV;

    return (
      <Alert variant="info" className="m-2">
        <p className="mb-2">Unable to display this component.</p>
        {isDevelopment && error && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: '0.9rem' }}>Details</summary>
            <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>{error.toString()}</pre>
          </details>
        )}
        <Button variant="info" size="sm" onClick={this.handleReset}>
          Retry
        </Button>
      </Alert>
    );
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback, level = 'component' } = this.props;

    if (!hasError) {
      return children;
    }

    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Render appropriate error UI based on level
    if (level === 'root') {
      return this.renderRootError();
    }

    if (level === 'feature') {
      return this.renderFeatureError();
    }

    return this.renderComponentError();
  }
}

export default ErrorBoundary;
